import axios, { AxiosInstance } from 'axios';
import { wixAxiosInstanceConfig } from '@wix/wix-axios-config';
import { GetLabelsResponse } from '@wix/ambassador-inbox-labels-server/types';
import { UserDataWithHasPassword } from '@wix/ambassador-wix-html-login-webapp/types';
import { ListUserPremiumAssetsResponse } from '@wix/ambassador-premium-data-view-retriever-server/types';
import {
  Email as ContactEmail,
  Phone as ContactPhone,
} from '@wix/ambassador-crm-inbox-server';
import {
  AppSettingsService,
  Host,
  State,
} from '@wix/ambassador-app-settings-service/http';
import { INBOX_ONBOARDING_REAL_ESTATE_ID } from '../constants/inboxConstants';
import { IOnboardingData } from '../features/InboxOnboarding/InboxOnboarding';
import { GetMyMemberResponse } from '@wix/ambassador-members-ng-api/http';
import { BlockModalType } from '../components/Controllers/ReportAndBlockModal/BlockModalType';

export type UserDataDto = UserDataWithHasPassword;

export enum IntegrationStatus {
  ACTIVE = 'ACTIVE',
  INVALID = 'INVALID',
  DISCONNECTED = 'DISCONNECTED',
}

export interface InboxSettingsDto {
  inbox: {
    subject?: string;
    mailSignature?: string;
    businessName?: string;
    fullname?: string;
  };
  integrations: {
    gmail:
      | {
          status: IntegrationStatus.ACTIVE | IntegrationStatus.INVALID;
          connectedEmailAddress: string;
        }
      | {
          status: IntegrationStatus.DISCONNECTED;
        };
    facebook:
      | {
          status: IntegrationStatus.ACTIVE | IntegrationStatus.INVALID;
          page: {
            name: string;
            id: string;
          };
        }
      | {
          status: IntegrationStatus.DISCONNECTED;
        };
  };
  wixUserData: {
    mailboxPurchasedFromWix?: string;
    wasOneAppInstalled: boolean;
  };
}

export enum SiteMemberStatus {
  ACTIVE = 'ACTIVE',
  DENIED = 'DENIED',
  APPLICANT = 'APPLICANT',
}

export interface ContactDto {
  chatroomId: string;
  contactId: string;
  displayName: string;
  fullName?: {
    firstName: string;
    lastName: string;
  };
  emails: ContactEmail[];
  phones: ContactPhone[];
  initials: string;
  picture: string;
  siteMemberStatus: SiteMemberStatus;
  siteUserType: 'Contact' | 'Visitor' | 'AscendBot';
  isDeleted: boolean;
}

export interface ContributorsDto {
  id: string;
  role: string;
  email: string;
  userName: string;
  profileImage?: string;
  lastSeen?: number;
}

export interface OnlinePeriod {
  start: number;
  end: number;
}
export interface AvailabilityHours {
  onlinePeriods: OnlinePeriod[];
}

export interface Permissions {
  permissions: string[];
}

export enum BlockAndReportType {
  BlockPhone,
  ReportSpam,
  DeleteChatroom,
}

export type Status = 'Online' | 'Offline';

export const axiosInstance: AxiosInstance = wixAxiosInstanceConfig(axios, {
  baseURL: `/`,
});
export const contactsApiPrefix = '/contacts-server/api/v1/metasite';
export const inboxUrlPrefix = '/_api/crm-inbox-server';
export const availabilityHoursPrefix = '/_api/availability-hours-server';
export const coreChatUrlPrefix = `${inboxUrlPrefix}/core-chat`;
export const pingSettingsPrefix = '/_api/ping/settings';
export const inboxLabelsUrlPrefix = `_api/inbox-labels-server/v1`;
export const inboxSettingsUrlPrefix = `_api/inbox-settings-server/v1/inbox/settings`;
export const accountSettingsUrlPrefix = `_api/account-settings-server`;
export const premiumAssetsUrlPrefix = `_api/premium-assets/v1`;
export const appSettingsUrl = `/_api/app-settings-service`;

const getAppSettingsService = () =>
  AppSettingsService(appSettingsUrl).AppSettingsService()({
    Authorization:
      axiosInstance.defaults.headers.common['X-Wix-Inbox-Instance'],
  });

const getDealerService = async () => {
  const dealer = await import(/* inbox-dealer */ './dealer');
  return new dealer.DealerService(
    axiosInstance.defaults.headers.common['X-Wix-Inbox-Instance'],
  );
};

const getMembersService = async () => {
  const membersModule = await import('@wix/ambassador-members-ng-api/http');
  return membersModule.MembersNgApi('/_api/members').Members()({
    Authorization:
      axiosInstance.defaults.headers.common['X-Wix-Inbox-Instance'],
  });
};

export const init = ({ instance }) => {
  axiosInstance.defaults.headers.common['X-Wix-Inbox-Instance'] = instance;
};

export const getChatToken = (): Promise<{ token: string }> => {
  return axiosInstance
    .post(`${coreChatUrlPrefix}/tokens`)
    .then((res) => res.data);
};

export const migrateAsync = (): Promise<any> => {
  return axiosInstance
    .post(`${coreChatUrlPrefix}/migrate-async`)
    .then((res) => res.data);
};

export const initAscendBot = async () => {
  await axiosInstance.post(`${inboxUrlPrefix}/ascend/bot/init`);
};

export const getContactInfo = (obj: {
  contactId?: string;
  chatroomId?: string;
}): Promise<Partial<ContactDto>> => {
  return axiosInstance
    .put(`${coreChatUrlPrefix}/conversations`, obj)
    .then((res) => res.data);
};

export const getBatchContactInfo = ({
  contactIds,
}: {
  contactIds: string[];
}): Promise<{ results: { [contactId: string]: ContactDto } }> => {
  return axiosInstance
    .post(`${inboxUrlPrefix}/contacts`, { contactIds })
    .then((res) => res.data);
};

export const getMyMember = async (): Promise<GetMyMemberResponse> => {
  return (await getMembersService()).getMyMember({ fieldSet: 'FULL' });
};

export const getChatSettings = (): Promise<{
  chatVisible: boolean;
  chatOnboarded: boolean;
  onboardingSource: string;
}> => {
  return axiosInstance
    .get(`${inboxUrlPrefix}/chat/settings`)
    .then((res) => res.data);
};

export const getSettings = (): Promise<InboxSettingsDto> => {
  return axiosInstance
    .get(`${inboxUrlPrefix}/v2/settings`)
    .then((res) => res.data);
};

export const getAppSettings = () => {
  return getAppSettingsService().getGlobalSettings({
    host: Host.BUSINESS_MANAGER,
    state: State.NR,
  });
};

export const updateAppSettings = (partialSettings: { [key: string]: any }) => {
  return getAppSettingsService().setGlobalSettings({
    settings: partialSettings,
    host: Host.BUSINESS_MANAGER,
    state: State.NR,
  });
};

export const getOnboardingData = async () => {
  return (await getDealerService()).getOfferingData<IOnboardingData>(
    INBOX_ONBOARDING_REAL_ESTATE_ID,
  );
};

export const getUserData = (): Promise<UserDataDto> => {
  return axiosInstance
    .get(`${accountSettingsUrlPrefix}/users`)
    .then((res) => res.data);
};

export const sendVerificationEmail = (): Promise<void> => {
  return axiosInstance
    .post(`${accountSettingsUrlPrefix}/send-verification-email`)
    .then((res) => res.data);
};

export const getPremiumAssets = (): Promise<ListUserPremiumAssetsResponse> => {
  return axiosInstance
    .get(`${premiumAssetsUrlPrefix}/assets`)
    .then((res) => res.data);
};

export const setChatVisibility = (chatVisible: boolean): Promise<any> => {
  return axiosInstance
    .put(`${inboxUrlPrefix}/chat/settings`, { chatVisible })
    .then((res) => res.data);
};

export const getContributors = (metaSiteId: string): Promise<any> => {
  return axiosInstance
    .get(`_api/crm-inbox-server/v2/authorization/sites/${metaSiteId}/users`)
    .then((res) => res.data);
};

export const getUploadToken = (): Promise<{
  token: string;
  url: string;
  folderId?: string;
}> => {
  return axiosInstance
    .post(`${inboxUrlPrefix}/media/upload-token`)
    .then((res) => res.data);
};

export const approveSiteMember = (
  msid: string,
  contactId: string,
): Promise<any> => {
  // we are actually getting contact Dto (not ours - the real from contacts)
  return axiosInstance
    .post(`${contactsApiPrefix}/${msid}/approveContact`, { contactId })
    .then((res) => res.data);
};

export const blockSiteMember = (
  msid: string,
  contactId: string,
): Promise<any> => {
  // we are actually getting contact Dto (not ours - the real from contacts)
  return axiosInstance
    .post(`${contactsApiPrefix}/${msid}/blockContact`, { contactId })
    .then((res) => res.data);
};

export const getHoursAvailabilityStatus = (): Promise<AvailabilityHours> => {
  return axiosInstance
    .get(`${availabilityHoursPrefix}/v1/status`)
    .then((res) => res.data);
};

export const getPingSettings = (msId: string): Promise<boolean> => {
  return axiosInstance
    .get(`${pingSettingsPrefix}/setting/root.site=${msId}?channel=BROWSER`)
    .then((res) => res.data.groupSettings.settingsData.mute);
};

export const setHoursAvailabilityStatus = (status: Status): Promise<any> => {
  return axiosInstance
    .put(`${availabilityHoursPrefix}/v1/status`, { status })
    .then((res) => res.data);
};

export const reportSpam = (chatroomId: string): Promise<void> => {
  return axiosInstance
    .post(`${coreChatUrlPrefix}/chatrooms/${chatroomId}/spam`)
    .then((res) => res.data);
};

export const reportBlockNumber = (
  chatroomId: string,
  blockReportType: BlockModalType,
): Promise<void> => {
  const needsToReport =
    blockReportType === BlockModalType.ReportAsSpam ||
    blockReportType === BlockModalType.ReportAndBlock;
  const needsToBlock =
    blockReportType === BlockModalType.Block ||
    blockReportType === BlockModalType.ReportAndBlock;

  const deleteOperation = [BlockAndReportType.DeleteChatroom];
  const reportOperation = needsToReport ? [BlockAndReportType.ReportSpam] : [];
  const blockOperation = needsToBlock ? [BlockAndReportType.BlockPhone] : [];

  const blockAndReportOperations = [
    ...deleteOperation,
    ...reportOperation,
    ...blockOperation,
  ];

  return axiosInstance
    .post(
      `${coreChatUrlPrefix}/chatrooms/${chatroomId}/block-and-report`,
      { blockAndReportOperations },
      {
        headers: {
          Authorization:
            axiosInstance.defaults.headers.common['X-Wix-Inbox-Instance'],
        },
      },
    )
    .then((res) => res.data);
};

export const getPermissions = (): Promise<Permissions> => {
  return axiosInstance
    .get(`${inboxUrlPrefix}/v2/permissions`)
    .then((res) => res.data);
};

export const getInboxLabels = () => {
  return axiosInstance
    .get(`${inboxLabelsUrlPrefix}/labels`, {
      headers: {
        Authorization:
          axiosInstance.defaults.headers.common['X-Wix-Inbox-Instance'],
      },
    })
    .then((res) => (res.data as GetLabelsResponse).labels);
};

export const getSitePages = () => {
  return axiosInstance
    .get(`${inboxSettingsUrlPrefix}/site-pages`, {
      headers: {
        Authorization:
          axiosInstance.defaults.headers.common['X-Wix-Inbox-Instance'],
      },
    })
    .then((res) => res.data);
};

export const openAscendBotChatroom = async () => {
  await axiosInstance.post(
    '_api/ascend-bot-server/v1/chatroom-opened',
    {},
    {
      headers: {
        Authorization:
          axiosInstance.defaults.headers.common['X-Wix-Inbox-Instance'],
      },
    },
  );
};

export interface IBusinessInfo {
  businessLogo?: string;
  businessName?: string;
  fullName?: string;
}

export async function getBusinessInfo() {
  const {
    data: { businessName, businessLogo, fullname: fullName },
  } = await axiosInstance.get(`${inboxUrlPrefix}/v2/settings/business`);
  return {
    businessName,
    businessLogo,
    fullName,
  } as IBusinessInfo;
}

export async function setBusinessInfo({
  fullName: fullname = '',
  businessLogo,
  businessName,
}: IBusinessInfo) {
  businessLogo = businessLogo && businessLogo.trim();
  await axiosInstance.post(`${inboxUrlPrefix}/v2/settings/business`, {
    fullname,
    businessName,
    businessLogo: businessLogo ? businessLogo : undefined,
  });
}
